// react
import type { ReactNode } from 'react';
// libraries
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

// material ui
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import type { CheckboxProps, FormControlLabelProps } from '@mui/material';

export type RHFCheckboxProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: ReactNode;
  rules?: UseControllerProps<TFieldValues>['rules'];
  checkboxProps?: Omit<CheckboxProps, 'name' | 'checked' | 'onChange'>;
} & Omit<FormControlLabelProps, 'control' | 'label' | 'name'>;

export default function RHFCheckbox<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  rules,
  checkboxProps,
  ...formControlLabelProps
}: RHFCheckboxProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormControl error={!!fieldState.error}>
          <FormControlLabel
            {...formControlLabelProps}
            control={
              <Checkbox
                {...checkboxProps}
                checked={!!field.value}
                onChange={(event) => field.onChange(event.target.checked)}
                onBlur={field.onBlur}
                slotProps={{
                  input: {
                    ref: field.ref
                  }
                }}
              />
            }
            label={label}
          />

          {fieldState.error?.message ? (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
}
